/* ============================================================================
   KETTLEBELL MONTH CAMPAIGN — runtime
   Powers sections/kettlebell-month-*.liquid. Ships no third-party JS.

   Five jobs:
     1. Configurator (#configure) — a real variant selector over the product's
        own variant JSON. Selecting an option updates the image, the caption,
        the price, the settings note, the derived pair-saving badge, the
        ?variant= query, and broadcasts the selection to the bundle module.
     2. Bundle builder (#bundle) — totals live variant prices client-side and
        applies the section's discount percentage. Every number, including the
        saving, is computed here; nothing is authored. Its button adds the whole
        ticked set to the cart in one request — the bell at the configurator's
        current variant — then applies the discount code the offer runs on.
     3. Collection grid (#collection) — tag filtering with a live count line.
     4. Accessories (#accessories) — real AJAX add-to-cart whose "ADDED"
        state is read back from /cart.js, so it survives a reload.
     5. Which-weight picker (#which-weight) — swaps the recommendation panel.
        The FAQ accordion is native <details> and needs nothing from here
        unless its "one question open at a time" setting is on.
     6. Media (#features and anywhere else the media snippet is used) — nothing
        autoplays. Every clip waits to be pressed, starts with sound, only one
        plays at a time, and anything that scrolls out of view pauses. Social
        embeds are injected on first press, never eagerly.

   Cross-module contract: the configurator dispatches `km:variant` on
   `document` with { sectionId, variantId, price, available, label, options }.
   The bundle module listens and re-totals. That is the only coupling, and it
   is one-way, so either module works with the other absent.
   ========================================================================== */
(function () {
  'use strict';

  /* Every kettlebell-month-* section renders snippets/kettlebell-month-assets,
     so a page using all eleven parses eleven identical <script> tags.
     Duplicate tags are fetched once but EXECUTED once each, which would give
     each run its own listeners and double every click. Bail after the first,
     exactly as the creator-promo runtime does. */
  if (window.__kettlebellMonthRuntime) return;
  window.__kettlebellMonthRuntime = true;

  var VARIANT_EVENT = 'km:variant';

  /* --- Money -------------------------------------------------------------
     Mirrors Liquid's `money` filter so a price computed in the browser is
     formatted identically to one rendered by the server. The format string
     comes from the shop, never from a settings field a human types — the
     campaign runs on four separate storefronts with four currencies. */
  function moneyFormat() {
    return (window.KMMoneyFormat || (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}');
  }

  function group(number, decimals, thousands, decimal) {
    if (isNaN(number) || number === null) return '0';
    var fixed = (number / 100).toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return whole + cents;
  }

  /* `cents` is an integer in the shop's minor unit, as Shopify reports it. */
  function money(cents) {
    var format = moneyFormat();
    var value = '';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var placeholder = match ? match[1] : 'amount';

    switch (placeholder) {
      case 'amount':
        value = group(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', ',');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* --- Small helpers ----------------------------------------------------- */
  function readJson(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function setText(root, selector, text) {
    var node = root.querySelector(selector);
    if (node) node.textContent = text;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* Fills {n} / {pct} / {range} style tokens in an authored string, so the
     discount percentage in a headline can never contradict the arithmetic. */
  function fill(template, values) {
    if (!template) return '';
    return template.replace(/\{(\w+)\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  /* ======================================================================
     1 · CONFIGURATOR
     ====================================================================== */
  function Configurator(root) {
    this.root = root;
    this.sectionId = root.dataset.kmSection || '';
    this.data = readJson(root, '[data-km-variants]') || {};
    this.variants = this.data.variants || [];
    /* roles maps a semantic name onto a 1-based Shopify option position, so
       the section works whatever the merchant named the options. A role the
       product does not have is simply absent. */
    this.roles = this.data.roles || {};
    this.notes = this.data.notes || {};
    this.tints = this.data.tints || {};
    this.overrides = this.data.images || [];
    this.syncUrl = root.dataset.kmSyncUrl === 'true';

    this.selection = {};
    this.bind();
    this.restore();
    this.update();
  }

  Configurator.prototype.bind = function () {
    var self = this;

    each(this.root.querySelectorAll('[data-km-option]'), function (chip) {
      chip.addEventListener('click', function () {
        self.selection[chip.dataset.kmOption] = chip.dataset.kmValue;
        self.update();
      });
    });

    var cta = this.root.querySelector('[data-km-add]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        /* An "add to cart" CTA is a button and adds; a "go to the PDP" CTA is
           rendered as a link and never reaches this handler. */
        event.preventDefault();
        self.add(cta);
      });
    }
  };

  /* Seed from ?variant= so a campaign link can preselect a colourway, then
     fall back to whichever chips Liquid marked active. */
  Configurator.prototype.restore = function () {
    var wanted = null;
    try {
      wanted = new URL(window.location.href).searchParams.get('variant');
    } catch (error) {
      wanted = null;
    }

    var seed = null;
    if (wanted) {
      seed = this.variants.filter(function (variant) {
        return String(variant.id) === String(wanted);
      })[0];
    }
    if (!seed) {
      seed = this.variants.filter(function (variant) {
        return variant.available;
      })[0] || this.variants[0];
    }
    if (!seed) return;

    var self = this;
    Object.keys(this.roles).forEach(function (role) {
      var index = Number(self.roles[role]) - 1;
      if (index >= 0 && seed.options[index] != null) {
        self.selection[role] = seed.options[index];
      }
    });
  };

  /* Every variant whose options match the current selection on the given
     roles. Passing an override lets the pair-saving lookup ask "same bell,
     but the other count". */
  Configurator.prototype.match = function (override) {
    var self = this;
    var wanted = Object.assign({}, this.selection, override || {});

    return this.variants.filter(function (variant) {
      return Object.keys(self.roles).every(function (role) {
        var index = Number(self.roles[role]) - 1;
        if (index < 0) return true;
        if (wanted[role] == null) return true;
        return variant.options[index] === wanted[role];
      });
    })[0] || null;
  };

  /* The most specific campaign override wins; blank fields are wildcards. A
     match on two roles beats a match on one. */
  Configurator.prototype.override = function () {
    var wanted = this.selection;
    var best = null;
    var bestScore = -1;

    this.overrides.forEach(function (entry) {
      var score = 0;
      var roles = ['weight', 'colour', 'count'];

      for (var i = 0; i < roles.length; i += 1) {
        var value = entry[roles[i]];
        if (!value) continue;
        if (value !== wanted[roles[i]]) return;
        score += 1;
      }

      if (score > bestScore) {
        best = entry;
        bestScore = score;
      }
    });

    return best;
  };

  Configurator.prototype.update = function () {
    var variant = this.match();
    var root = this.root;

    /* Chip states, plus a strike-through on combinations the store cannot
       sell right now. */
    var self = this;
    each(root.querySelectorAll('[data-km-option]'), function (chip) {
      var role = chip.dataset.kmOption;
      var value = chip.dataset.kmValue;
      var active = self.selection[role] === value;
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');

      var candidate = {};
      candidate[role] = value;
      var reachable = self.match(candidate);
      chip.classList.toggle('is-unavailable', !reachable || !reachable.available);
    });

    /* Image: the selected variant's featured image, unless a campaign
       override is set for this combination. */
    var override = this.override();
    var image = root.querySelector('[data-km-image]');
    var source = null;
    var alt = '';

    if (override && override.src) {
      source = override.src;
      alt = override.alt || '';
    } else if (variant && variant.image) {
      source = variant.image;
      alt = variant.image_alt || '';
    }

    if (image && source) {
      if (image.getAttribute('src') !== source) image.setAttribute('src', source);
      /* A variant-specific srcset from the previous selection would keep
         serving the old colourway on wide viewports. */
      image.removeAttribute('srcset');
      if (alt) image.setAttribute('alt', alt);
      var well = image.closest('.km-media');
      if (well) well.classList.add('has-image');
    }

    var media = root.querySelector('[data-km-tint]');
    if (media) {
      var tint = this.tints[this.selection.colour];
      media.style.setProperty('--km-cfg-tint', tint || '');
    }

    /* Caption: "{range} · {colour} · {single|pair}", skipping roles the
       product does not expose. */
    var parts = ['weight', 'colour', 'count']
      .map(function (role) {
        return self.selection[role];
      })
      .filter(Boolean);
    setText(root, '[data-km-caption]', parts.join(' · '));
    setText(root, '[data-km-source]', root.dataset.kmSourceNote
      ? fill(root.dataset.kmSourceNote, { colour: this.selection.colour || '', count: this.selection.count || '' })
      : '');

    /* Price and the settings note for the selected range. */
    if (variant) {
      setText(root, '[data-km-price]', money(variant.price));
      var was = root.querySelector('[data-km-compare]');
      if (was) {
        var show = variant.compare_at_price && variant.compare_at_price > variant.price;
        was.hidden = !show;
        if (show) was.textContent = money(variant.compare_at_price);
      }
    }
    setText(root, '[data-km-settings]', this.notes[this.selection.weight] || '');

    /* Derived pair saving. Computed from the two real variant prices and
       hidden when the arithmetic gives nothing away — a typed "SAVE 10%"
       that contradicts the prices is the bug this guards against. */
    var badge = root.querySelector('[data-km-pair-saving]');
    if (badge) {
      var pairValue = root.dataset.kmPairValue;
      var singleValue = root.dataset.kmSingleValue;
      var pct = 0;

      if (pairValue && singleValue) {
        var pair = this.match({ count: pairValue });
        var single = this.match({ count: singleValue });
        if (pair && single && single.price > 0) {
          pct = Math.round((1 - pair.price / (single.price * 2)) * 100);
        }
      }

      badge.hidden = pct <= 0;
      if (pct > 0) {
        badge.textContent = fill(badge.dataset.kmTemplate || 'Save {n}% on the pair', { n: pct });
      }
    }

    /* CTA availability. */
    var cta = root.querySelector('[data-km-add]');
    if (cta) {
      var sellable = !!(variant && variant.available);
      cta.disabled = !sellable;
      cta.textContent = sellable
        ? cta.dataset.kmLabel || 'Add to cart'
        : cta.dataset.kmSoldOut || 'Sold out';
      if (variant) cta.dataset.kmVariantId = variant.id;
    }

    /* Deep-linkable selection. replaceState, not pushState — the back button
       should leave the page, not walk the shopper's colour choices. */
    if (this.syncUrl && variant) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* Old Safari without URL(); the selection still works. */
      }
    }

    document.dispatchEvent(
      new CustomEvent(VARIANT_EVENT, {
        detail: {
          sectionId: this.sectionId,
          variantId: variant ? variant.id : null,
          price: variant ? variant.price : null,
          available: variant ? variant.available : false,
          label: parts.join(' · '),
          options: Object.assign({}, this.selection)
        }
      })
    );
  };

  Configurator.prototype.add = function (button) {
    var variant = this.match();
    if (!variant || !variant.available) return;

    var root = this.root;
    var error = root.querySelector('[data-km-error]');
    var original = button.dataset.kmLabel || button.textContent.trim();

    button.disabled = true;
    button.textContent = '…';
    if (error) error.hidden = true;

    addToCart([{ id: Number(variant.id), quantity: 1 }])
      .then(function () {
        openCart(button);
      })
      .catch(function (reason) {
        if (error) {
          error.textContent = String(reason && reason.message ? reason.message : '').trim() ||
            button.dataset.kmErrorText || 'Sorry, something went wrong.';
          error.hidden = false;
        }
      })
      .then(function () {
        button.disabled = false;
        button.textContent = original;
      });
  };

  /* --- Cart ------------------------------------------------------------
     One shared path for every add on the page, so the theme's drawer always
     behaves the way it does for a normal PDP add. */
  function addToCart(items) {
    var routes = (window.theme && window.theme.routes) || {};

    return fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (response.ok) return response.json();

      /* Shopify returns 422 with a useful description, e.g. an inventory cap.
         Prefer it over a generic message so the shopper knows what blocked
         the add. */
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (body) {
          throw new Error(body.description || body.message || '');
        });
    });
  }

  function openCart(opener) {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    document.dispatchEvent(
      new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: opener } })
    );
  }

  /* Variant ids currently in the cart, so an "ADDED" state reflects the cart
     rather than a click that happened in this page view. */
  var cartIds = null;
  var cartPromise = null;

  function loadCart(force) {
    if (cartPromise && !force) return cartPromise;

    cartPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : { items: [] };
      })
      .catch(function () {
        return { items: [] };
      })
      .then(function (cart) {
        cartIds = (cart.items || []).map(function (item) {
          return String(item.variant_id);
        });
        return cartIds;
      });

    return cartPromise;
  }

  /* ======================================================================
     2 · BUNDLE BUILDER
     ====================================================================== */
  function Bundle(root) {
    this.root = root;
    this.pct = Number(root.dataset.kmDiscountPct || 0);
    this.watch = root.dataset.kmWatchSection || '';
    /* 'derived' = the real share saved on the whole set; 'discount' = the
       offer's own percentage. Both come out of the arithmetic — the setting
       only picks which of the two the tag states. */
    this.saveSource = root.dataset.kmSaveSource === 'discount' ? 'discount' : 'derived';
    /* 'subtotal' = every paid line in the set is discounted (the default);
       'bell' = the kettlebell only, which is what the original brief asked
       for. Free lines are outside the base in both cases. */
    this.discountBase = root.dataset.kmDiscountBase === 'bell' ? 'bell' : 'subtotal';
    this.discountCode = (root.dataset.kmDiscountCode || '').trim();
    this.afterAdd = root.dataset.kmAfterAdd || 'cart';
    this.items = [];
    this.bell = null;

    var self = this;
    each(root.querySelectorAll('[data-km-line]'), function (line) {
      var item = {
        el: line,
        key: line.dataset.kmLine,
        price: Number(line.dataset.kmPrice || 0),
        /* The variant this line puts in the cart. The bell's is replaced by the
           configurator's current selection over `km:variant` — a bundle that
           adds the default colourway regardless of what the shopper picked is
           the whole point of wiring these two modules together. */
        variantId: line.dataset.kmVariantId || '',
        name: line.dataset.kmName || '',
        /* A line the offer grants by itself is priced and totalled here but
           never posted to the cart, so nothing labelled Free gets charged. */
        addToCart: line.dataset.kmAdd !== 'false',
        isBell: line.dataset.kmBell === 'true',
        free: line.dataset.kmFree === 'true',
        required: line.dataset.kmRequired === 'true',
        qualifies: line.dataset.kmQualifies === 'true',
        /* A required or free line is not a toggle, so it starts in and stays
           in; everything else honours its authored default. */
        included: line.dataset.kmDefault !== 'false'
      };

      if (item.required || item.free) item.included = true;
      if (item.isBell) self.bell = item;
      self.items.push(item);

      if (!item.required && !item.free) {
        line.addEventListener('click', function () {
          item.included = !item.included;
          self.update();
        });
      }
    });

    var addBtn = root.querySelector('[data-km-bundle-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        self.submit(addBtn);
      });
    }

    /* The bell's price follows the configurator, so it is never authored. */
    document.addEventListener(VARIANT_EVENT, function (event) {
      var detail = event.detail || {};
      if (self.watch && detail.sectionId && detail.sectionId !== self.watch) return;
      if (!self.bell || detail.price == null) return;

      self.bell.price = detail.price;
      self.bell.label = detail.label || '';
      if (detail.variantId) self.bell.variantId = String(detail.variantId);
      self.update();
    });

    this.update();
  }

  Bundle.prototype.math = function () {
    var subtotal = 0;
    var count = 0;

    this.items.forEach(function (item) {
      if (item.free || !item.included) return;
      subtotal += item.price;
      count += 1;
    });

    /* Qualifying means the bell plus every line flagged as qualifying. */
    var qualified =
      !!(this.bell && this.bell.included) &&
      this.items
        .filter(function (item) {
          return item.qualifies;
        })
        .every(function (item) {
          return item.included;
        });

    /* `subtotal` already excludes free lines, so neither base ever discounts
       something the shopper is not paying for. */
    var bellPrice = this.bell ? this.bell.price : 0;
    var base = this.discountBase === 'bell' ? bellPrice : subtotal;
    var discount = qualified ? Math.round(base * (this.pct / 100)) : 0;

    return {
      subtotal: subtotal,
      count: count,
      qualified: qualified,
      discount: discount,
      total: subtotal - discount,
      /* Saving as a share of what the shopper would otherwise pay — the
         figure the SAVE tag shows, so it can never contradict the total. With
         the subtotal base this equals the discount percentage; with the bell
         base it is smaller, which is the honest number. */
      savePct: subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0
    };
  };

  Bundle.prototype.update = function () {
    var root = this.root;
    var math = this.math();
    var pct = this.pct;

    this.items.forEach(function (item) {
      if (!item.required && !item.free) {
        item.el.setAttribute('aria-pressed', item.included ? 'true' : 'false');
      }

      /* Free lines render their own "FREE" copy in Liquid — leave it alone. */
      var priceNode = item.el.querySelector('[data-km-line-price]');
      if (priceNode && !item.free) priceNode.textContent = money(item.price);
    });

    setText(
      root,
      '[data-km-subtotal-label]',
      fill(root.dataset.kmSubtotalTemplate || '{n} items · subtotal', {
        n: math.count,
        items: math.count === 1 ? 'item' : 'items'
      })
    );
    setText(root, '[data-km-subtotal]', money(math.subtotal));

    setText(
      root,
      '[data-km-discount-label]',
      math.qualified
        ? fill(root.dataset.kmDiscountLabel || '{n}% off the bell', { n: pct })
        : fill(root.dataset.kmDiscountPrompt || 'Add the add-ons for {n}% off', { n: pct })
    );
    setText(root, '[data-km-discount]', math.qualified ? '−' + money(math.discount) : '—');

    setText(root, '[data-km-total]', money(math.total));

    var was = root.querySelector('[data-km-was]');
    if (was) {
      was.hidden = !math.qualified;
      was.textContent = money(math.subtotal);
    }

    var tag = root.querySelector('[data-km-save]');
    if (tag) {
      var savePct = this.saveSource === 'discount' ? pct : math.savePct;
      /* Hidden on a zero saving either way, so a 0% campaign or a
         disqualified set never shows a tag claiming nothing. */
      tag.hidden = !math.qualified || savePct <= 0;
      tag.textContent = fill(tag.dataset.kmTemplate || 'Save {n}%', { n: savePct });
    }

    setText(
      root,
      '[data-km-note]',
      math.qualified
        ? fill(root.dataset.kmNoteQualified || '', {
            n: pct,
            variant: (this.bell && this.bell.label) || ''
          })
        : fill(root.dataset.kmNoteUnqualified || '', { n: pct })
    );

    var heading = root.querySelector('[data-km-heading]');
    if (heading && heading.dataset.kmTemplate) {
      heading.textContent = fill(heading.dataset.kmTemplate, { n: pct });
    }
  };

  /* Everything the shopper has ticked, as a /cart/add.js payload. Free lines
     and lines the offer grants itself are excluded; a line with no resolvable
     variant is excluded rather than silently posting a bad id. */
  Bundle.prototype.payload = function () {
    var items = [];

    this.items.forEach(function (item) {
      if (!item.included || !item.addToCart) return;
      var id = Number(item.variantId);
      if (!id) return;
      items.push({ id: id, quantity: 1 });
    });

    return items;
  };

  Bundle.prototype.submit = function (button) {
    var self = this;
    var items = this.payload();
    var error = this.root.querySelector('[data-km-bundle-error]');
    var original = button.dataset.kmLabel || button.textContent.trim();

    function fail(message) {
      if (!error) return;
      error.textContent = message || button.dataset.kmErrorText || 'Sorry, something went wrong.';
      error.hidden = false;
    }

    if (error) error.hidden = true;

    /* Nothing addable. Usually the configurator has not resolved a variant —
       the bell is a required line, so this is worth saying rather than leaving
       a button that appears to do nothing. */
    if (!items.length) {
      fail(button.dataset.kmErrorText);
      return;
    }

    button.disabled = true;
    button.textContent = button.dataset.kmBusyLabel || 'Adding…';

    addToCart(items)
      .then(function () {
        /* Shopify's cart API has no discount parameter, so a code can only be
           applied by visiting the /discount/ route. That is a navigation, which
           is why "stay on this page" and "apply the code" are mutually
           exclusive and the section says so. */
        if (self.discountCode && self.afterAdd !== 'drawer') {
          var target = self.afterAdd === 'checkout' ? '/checkout' : '/cart';
          window.location.href =
            '/discount/' + encodeURIComponent(self.discountCode) +
            '?redirect=' + encodeURIComponent(target);
          return;
        }

        if (self.afterAdd === 'checkout') {
          window.location.href = '/checkout';
          return;
        }

        if (self.afterAdd === 'cart') {
          window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
          return;
        }

        /* Drawer: no code applied, by definition. */
        openCart(button);
        button.disabled = false;
        button.textContent = original;
      })
      .catch(function (reason) {
        fail(String(reason && reason.message ? reason.message : '').trim());
        button.disabled = false;
        button.textContent = original;
      });
  };

  /* ======================================================================
     3 · COLLECTION GRID FILTERS
     ====================================================================== */
  function Grid(root) {
    var cards = root.querySelectorAll('[data-km-card]');
    var chips = root.querySelectorAll('[data-km-filter]');
    var count = root.querySelector('[data-km-grid-count]');
    var template = root.dataset.kmCountTemplate || '{shown} of {total} shown';
    var total = cards.length;

    function apply(filter) {
      var shown = 0;

      each(cards, function (card) {
        var tags = (card.dataset.kmTags || '').split('|').filter(Boolean);
        var visible = !filter || tags.indexOf(filter) !== -1;
        card.hidden = !visible;
        if (visible) shown += 1;
      });

      if (count) count.textContent = fill(template, { shown: shown, total: total });

      var empty = root.querySelector('[data-km-grid-empty]');
      if (empty) empty.hidden = shown > 0;
    }

    each(chips, function (chip) {
      chip.addEventListener('click', function () {
        each(chips, function (other) {
          other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
        });
        apply(chip.dataset.kmFilter);
      });
    });

    var active = root.querySelector('[data-km-filter][aria-pressed="true"]');
    apply(active ? active.dataset.kmFilter : '');
  }

  /* ======================================================================
     4 · ACCESSORIES — real AJAX add-to-cart
     ====================================================================== */
  function Accessories(root) {
    var buttons = root.querySelectorAll('[data-km-acc-add]');
    if (!buttons.length) return;

    function paint(ids) {
      each(buttons, function (button) {
        var inCart = ids.indexOf(String(button.dataset.kmVariantId)) !== -1;
        button.classList.toggle('is-added', inCart);
        button.textContent = inCart
          ? button.dataset.kmAddedLabel || 'Added ✓'
          : button.dataset.kmLabel || 'Add to cart';
      });
    }

    loadCart().then(paint);

    each(buttons, function (button) {
      button.addEventListener('click', function () {
        var id = Number(button.dataset.kmVariantId);
        if (!id) return;

        var error = root.querySelector('[data-km-acc-error]');
        var original = button.textContent;

        button.disabled = true;
        button.textContent = '…';
        if (error) error.hidden = true;

        addToCart([{ id: id, quantity: 1 }])
          .then(function () {
            openCart(button);
            /* Re-read the cart rather than trusting the click, so the state
               is the cart's and survives a reload. */
            return loadCart(true).then(paint);
          })
          .catch(function (reason) {
            button.textContent = original;
            if (error) {
              error.textContent =
                String(reason && reason.message ? reason.message : '').trim() ||
                root.dataset.kmError ||
                'Sorry, something went wrong.';
              error.hidden = false;
            }
          })
          .then(function () {
            button.disabled = false;
          });
      });
    });

    /* Another module (or the drawer itself) changed the cart. */
    document.addEventListener('dispatch:cart-drawer:refresh', function () {
      loadCart(true).then(paint);
    });
  }

  /* ======================================================================
     6 · MEDIA — press-to-play video and social embeds
     ====================================================================== */

  /* Every player on the page, so starting one can stop the rest. */
  var players = [];

  function pauseOthers(current) {
    players.forEach(function (player) {
      if (player !== current) player.pause();
    });
  }

  /* Wraps one .km-media that carries video or an embed. Press-to-play only:
     there is no autoplay mode, by design. The handoff limits motion on this
     page to state changes and hover, and the visitor arrived from a link, not
     to be shouted at. */
  function Player(root) {
    this.root = root;
    this.video = root.querySelector('video');
    this.playBtn = root.querySelector('[data-km-play]');
    this.soundBtn = root.querySelector('[data-km-sound]');
    this.embed = root.dataset.kmEmbed || '';
    this.started = false;
    this.bind();
  }

  Player.prototype.bind = function () {
    var self = this;

    if (this.playBtn) {
      this.playBtn.addEventListener('click', function () {
        self.start();
      });
    }

    if (this.video) {
      /* Tapping the video itself toggles playback, matching the reel players
         elsewhere in the theme. */
      this.video.addEventListener('click', function () {
        if (self.video.paused) self.start();
        else self.pause();
      });

      this.video.addEventListener('play', function () {
        self.root.classList.add('is-playing');
        pauseOthers(self);
      });
      this.video.addEventListener('pause', function () {
        self.root.classList.remove('is-playing');
      });

      /* A finished clip fires `ended`, not `pause`, in Chrome — without this
         the poster would stay hidden and the play button would stay gone,
         leaving a dead last frame with no way to replay. */
      this.video.addEventListener('ended', function () {
        self.root.classList.remove('is-playing');
        self.video.currentTime = 0;
      });
    }

    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', function () {
        self.toggleSound();
      });
      this.syncSoundLabel();
    }
  };

  /* Injects the social embed on first interaction. Returns true when this
     player is embed-backed, so start() knows not to look for a <video>. */
  Player.prototype.mountEmbed = function () {
    if (!this.embed || this.started) return !!this.embed;

    var frame = document.createElement('iframe');
    frame.className = 'km-media__embed';
    frame.src = this.embed;
    frame.loading = 'lazy';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('title', this.root.dataset.kmEmbedTitle || '');
    this.root.appendChild(frame);

    this.started = true;
    this.root.classList.add('is-playing');
    pauseOthers(this);
    return true;
  };

  Player.prototype.start = function () {
    if (this.mountEmbed()) return;
    if (!this.video) return;

    /* The visitor pressed play, so they want sound. The element carries the
       `muted` attribute only so the browser never treats it as autoplayable. */
    this.video.muted = false;
    this.syncSoundLabel();

    var attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') {
      var self = this;
      attempt.catch(function (error) {
        /* NotAllowedError means the autoplay policy blocked unmuted playback —
           Safari in particular rejects when it decides the gesture wasn't
           user-initiated. Falling back to muted beats leaving a dead poster.

           Every other rejection must be left alone. AbortError in particular
           means something deliberately interrupted us, which on this page is
           almost always the IntersectionObserver pausing a player that just
           scrolled away; retrying there would fight the pause and restart a
           video the visitor has already scrolled past. */
        if (error && error.name !== 'NotAllowedError') return;
        self.video.muted = true;
        self.syncSoundLabel();
        self.video.play().catch(function () {});
      });
    }
    this.started = true;
  };

  Player.prototype.pause = function () {
    if (this.video && !this.video.paused) this.video.pause();
  };

  Player.prototype.toggleSound = function () {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    /* Unmuting a clip sitting on its poster should also start it. */
    if (!this.video.muted && this.video.paused) this.start();
    this.syncSoundLabel();
  };

  Player.prototype.syncSoundLabel = function () {
    if (!this.soundBtn || !this.video) return;
    var muted = this.video.muted;
    this.soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    this.soundBtn.setAttribute(
      'aria-label',
      (muted ? this.soundBtn.dataset.labelOn : this.soundBtn.dataset.labelOff) || ''
    );
  };

  /* Pause-only. Scrolling a playing clip out of view stops it, so audio never
     follows the visitor down the page; scrolling back does NOT resume it,
     because nothing on this page starts without being asked to. */
  var mediaObserver = null;
  if ('IntersectionObserver' in window) {
    mediaObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) return;
          var player = entry.target.kmPlayer;
          if (player) player.pause();
        });
      },
      { threshold: 0.25 }
    );
  }

  function initMedia(scope) {
    each(scope.querySelectorAll('[data-km-video]'), function (root) {
      if (root.kmPlayer) return;

      var player = new Player(root);
      root.kmPlayer = player;
      players.push(player);

      if (mediaObserver) mediaObserver.observe(root);
    });
  }

  /* ======================================================================
     5 · WHICH-WEIGHT PICKER + FAQ
     ====================================================================== */
  function Picker(root) {
    var chips = root.querySelectorAll('[data-km-level]');
    if (!chips.length) return;

    var range = root.querySelector('[data-km-level-range]');
    var body = root.querySelector('[data-km-level-body]');
    var cta = root.querySelector('[data-km-level-cta]');

    function show(chip) {
      each(chips, function (other) {
        other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
      });
      if (range) range.textContent = chip.dataset.kmRange || '';
      if (body) body.textContent = chip.dataset.kmBody || '';
      if (cta) {
        cta.textContent = chip.dataset.kmCtaLabel || '';
        cta.setAttribute('href', chip.dataset.kmCtaUrl || '#configure');
        cta.hidden = !chip.dataset.kmCtaLabel;
      }
    }

    each(chips, function (chip) {
      chip.addEventListener('click', function () {
        show(chip);
      });
    });

    var active = root.querySelector('[data-km-level][aria-pressed="true"]');
    show(active || chips[0]);
  }

  /* Native <details> already gives keyboard support, works with JS off, and
     expands and contracts on its own. The ONLY thing this adds is the optional
     single-open coordination, so an accordion left on its default setting runs
     no JavaScript at all — the early return below is the whole story. */
  function Faq(group) {
    if (group.dataset.kmFaqReady) return;
    if (group.dataset.kmSingle !== 'true') return;
    group.dataset.kmFaqReady = 'true';

    var items = Array.prototype.slice.call(group.querySelectorAll('details'));
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  function init(scope) {
    var root = scope || document;

    each(root.querySelectorAll('[data-km-configurator]'), function (node) {
      if (node.kmReady) return;
      node.kmReady = true;
      new Configurator(node);
    });

    each(root.querySelectorAll('[data-km-bundle]'), function (node) {
      if (node.kmReady) return;
      node.kmReady = true;
      new Bundle(node);
    });

    each(root.querySelectorAll('[data-km-grid]'), function (node) {
      if (node.kmReady) return;
      node.kmReady = true;
      new Grid(node);
    });

    each(root.querySelectorAll('[data-km-accessories]'), function (node) {
      if (node.kmReady) return;
      node.kmReady = true;
      new Accessories(node);
    });

    each(root.querySelectorAll('[data-km-picker]'), function (node) {
      if (node.kmReady) return;
      node.kmReady = true;
      new Picker(node);
    });

    each(root.querySelectorAll('[data-km-faq]'), Faq);

    initMedia(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: a re-rendered section brings new DOM with no listeners. The
     kmReady flags live on the elements themselves, so replaced DOM is
     initialised and surviving DOM is left alone. */
  document.addEventListener('shopify:section:load', function (event) {
    /* Drop players whose DOM was replaced, so pauseOthers() does not hold a
       growing list of detached nodes across editor edits. */
    players = players.filter(function (player) {
      return player.root.isConnected;
    });
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    players = players.filter(function (player) {
      return player.root.isConnected;
    });
  });
})();
